import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { cachedRequest } from "../../../utils/requestCache";

const getTenantUrl = () => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId)
    throw new Error(
      "Tenant ID not found in localStorage. Please select a tenant."
    );
  return `${API_BASE_URL}/company/?tenant_id=${tenantId}`;
};

const validateTenant = (): string => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) {
    throw new Error(
      "No tenant selected. Please select a tenant before performing operations."
    );
  }
  return tenantId;
};

const addTenantToCompany = (company: Partial<Company>): Partial<Company> => {
  const tenantId = validateTenant();

  return {
    ...company,
    tenant_id: parseInt(tenantId, 10),
  };
};

export interface Company {
  company_id?: number;
  crm_entity_id?: number;
  company_name: string;
  website: string;
  phone: string;
  tax_id: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  currency_code: string;
  annual_revenue: string;
  employee_count: string;
  linkedin_url: string;
  facebook_url: string;
  twitter_url: string;
  industry_type_id: number | null;
  business_type_id: number | null;
  assigned_datetime: string | null;
  time_zone: string;
  is_active: boolean;
  created_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant_id: number | null;
  parent_company: number | null;
  assigned_user:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    email: string;
    phone: string;
    avatar_url: string;
  };
  assigned_user_full_name?: string;
  company_logo?: string | null;
  assigned_team?: {
    team_id: number;
    team_name?: string;
    name?: string;
    manager?: {
      user_id: number;
      full_name: string;
      avatar_url: string;
    };
    logo_url?: string;
  } | null;

  created_at?: string;
  updated_at?: string;
  created_datetime?: string;
  updated_datetime?: string;

  business_type_display_name?: string;
  industry_type_display_name?: string;
  created_by_username?: string;
  updated_by_username?: string;
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;

  custom_fields?: Array<{
    custom_field_id: number;
    value: any;
  }>;
}

export interface PaginatedCompanyResponse {
  companies: Company[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
}

const normalizeCompanyData = (company: any): Company => {
  const normalizeUserObject = (userData: any, fieldName: string) => {
    if (!userData) return null;

    if (typeof userData === "object") {
      if (fieldName === "assigned_user") {
        if (userData.full_name || userData.user_id) {
          return userData;
        }
      } else {
        if (userData.full_name || userData.user_id) {
          return userData;
        }
      }
    }

    if (typeof userData === "number") {
      return userData;
    }

    return null;
  };

  return {
    ...company,

    company_id: company.company_id ? Number(company.company_id) : undefined,
    industry_type_id: company.industry_type_id
      ? Number(company.industry_type_id)
      : null,
    business_type_id: company.business_type_id
      ? Number(company.business_type_id)
      : null,
    created_by: normalizeUserObject(company.created_by, "created_by"),
    updated_by: normalizeUserObject(company.updated_by, "updated_by"),
    assigned_user: normalizeUserObject(company.assigned_user, "assigned_user"),
    parent_company: company.parent_company
      ? Number(company.parent_company)
      : null,
    tenant_id: company.tenant_id ? Number(company.tenant_id) : null,
    is_active: Boolean(company.is_active),

    created_at: company.created_at || company.created_datetime,
    updated_at: company.updated_at || company.updated_datetime,
    created_datetime: company.created_datetime || company.created_at,
    updated_datetime: company.updated_datetime || company.updated_at,

    company_name: String(company.company_name || ""),
    website: String(company.website || ""),
    phone: String(company.phone || ""),
    tax_id: String(company.tax_id || ""),
    address: String(company.address || ""),
    city: String(company.city || ""),
    state: String(company.state || ""),
    country: String(company.country || ""),
    zip_code: String(company.zip_code || ""),
    currency_code: String(company.currency_code || ""),
    annual_revenue: String(company.annual_revenue || ""),
    employee_count: String(company.employee_count || ""),
    linkedin_url: String(company.linkedin_url || ""),
    facebook_url: String(company.facebook_url || ""),
    twitter_url: String(company.twitter_url || ""),
    time_zone: String(company.time_zone || ""),

    business_type_display_name: company.business_type_display_name,
    industry_type_display_name: company.industry_type_display_name,
    created_by_username: company.created_by_username,
    updated_by_username: company.updated_by_username,
    company_logo: company.company_logo,
    assigned_team: company.assigned_team || null,
    custom_fields: company.custom_fields || undefined,
  };
};

export const CompanyService = {
  getCompaniesByPage: async (
    page: number = 1,
    pageSize: number = 12,
    search: string = "",
    lightMode: boolean = false
  ): Promise<PaginatedCompanyResponse> => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) {
        params.company_name = search;
      }

      if (lightMode) {
        params.response_mode = "light";
      }

      const authRaw = localStorage.getItem("authResponse");
      const tenantId = localStorage.getItem("selectedTenantId");

      if (authRaw && tenantId) {
        const auth = JSON.parse(authRaw);

        const tenantObj = Array.isArray(auth.tenants)
          ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
          : null;

        if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
          params.sales_user_id = auth.user_id;
        }
      }

      const response = await axios.get(getTenantUrl(), {
        headers: getAuthHeaders(),
        params,
      });

      let companies: Company[] = [];
      let totalCount = 0;

      if (response.data.results) {
        companies = response.data.results.map(normalizeCompanyData);
        totalCount = response.data.count ?? companies.length;
      } else if (Array.isArray(response.data)) {
        companies = response.data.map(normalizeCompanyData);
        totalCount = companies.length;
      } else {
        console.warn("Unexpected API response format:", response.data);
        return {
          companies: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page,
          hasMore: false,
        };
      }

      const totalPages = Math.ceil(totalCount / pageSize);
      const hasMore = page < totalPages;

      return {
        companies,
        totalCount,
        totalPages,
        currentPage: page,
        hasMore,
      };
    } catch (error) {
      console.error("Error fetching companies page:", error);
      return handleAuthError(error);
    }
  },

  getAllCompanies: async (): Promise<Company[]> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      return await cachedRequest(`companies:all:${tenantId}`, async () => {
        const response = await CompanyService.getCompaniesByPage(1, 1000);
        return response.companies;
      });
    } catch (error) {
      console.error("Error fetching companies:", error);
      return handleAuthError(error);
    }
  },

  getCompanyById: async (id: number): Promise<Company | null> => {
    try {
      const tenantId = validateTenant();

      const getUrl = `${API_BASE_URL}/company/${id}/`;
      const response = await axios.get(getUrl, {
        headers: getAuthHeaders(),
        params: { tenant_id: tenantId },
      });
      return response.data ? normalizeCompanyData(response.data) : null;
    } catch (error) {
      console.error("Error fetching company:", error);
      return handleAuthError(error);
    }
  },

  createCompany: async (
    companyData: Omit<Company, "company_id">
  ): Promise<Company> => {
    try {
      const companyWithTenant = addTenantToCompany(companyData);

      const response = await axios.post(
        `${API_BASE_URL}/company/`,
        companyWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );

      return normalizeCompanyData(response.data);
    } catch (error: any) {
      console.error("Error creating company:", error);
      return handleAuthError(error);
    }
  },

  createCompanyWithLogo: async (formData: FormData): Promise<Company> => {
    try {
      const tenantId = validateTenant();
      formData.append("tenant_id", tenantId);

      const logoFile = formData.get("company_logo") as File;
      if (logoFile) {
        const reader = new FileReader();
        const binaryData = await new Promise<ArrayBuffer>((resolve) => {
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.readAsArrayBuffer(logoFile);
        });
        formData.delete("company_logo");
        formData.append("company_logo", new Blob([binaryData]), logoFile.name);
      }

      const response = await axios.post(`${API_BASE_URL}/company/`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      return normalizeCompanyData(response.data);
    } catch (error: any) {
      console.error("Error creating company with logo:", error);
      return handleAuthError(error);
    }
  },

  updateCompany: async (
    id: number,
    companyData: Partial<Company>
  ): Promise<Company> => {
    try {
      const companyWithTenant = addTenantToCompany(companyData);

      const tenantId = validateTenant();

      const updateUrl = `${API_BASE_URL}/company/${id}/`;
      const response = await axios.patch(updateUrl, companyWithTenant, {
        headers: getAuthHeaders(),
        params: { tenant_id: tenantId },
      });
      return normalizeCompanyData(response.data);
    } catch (error) {
      console.error("Error updating company:", error);
      return handleAuthError(error);
    }
  },

  updateCompanyWithLogo: async (
    id: number,
    formData: FormData
  ): Promise<Company> => {
    try {
      const tenantId = validateTenant();
      formData.append("tenant_id", tenantId);

      const logoFile = formData.get("company_logo") as File;
      if (logoFile) {
        const reader = new FileReader();
        const binaryData = await new Promise<ArrayBuffer>((resolve) => {
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.readAsArrayBuffer(logoFile);
        });
        formData.delete("company_logo");
        formData.append("company_logo", new Blob([binaryData]), logoFile.name);
      }

      const updateUrl = `${API_BASE_URL}/company/${id}/`;
      const response = await axios.patch(updateUrl, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
        params: { tenant_id: tenantId },
      });
      return normalizeCompanyData(response.data);
    } catch (error) {
      console.error("Error updating company with logo:", error);
      return handleAuthError(error);
    }
  },

  deleteCompany: async (id: number): Promise<void> => {
    try {
      const tenantId = validateTenant();

      const deleteUrl = `${API_BASE_URL}/company/${id}/`;
      await axios.delete(deleteUrl, {
        headers: getAuthHeaders(),
        params: { tenant_id: tenantId },
      });
    } catch (error) {
      console.error("Error deleting company:", error);
      return handleAuthError(error);
    }
  },
};
